import { useFormState } from "common/hooks";
import { IPatientWrite, PatientMissingCheckoutField } from "common/types";
import { parseNumber } from "common/utils/numbers";
import { MONTH_SHORT_NAMES } from "common/constants/dates";
import { getWeightUnitOfMeasureLabel } from "common/utils/patients";

import { OnboardingDetailSubscriptionStepParams } from "client/onboarding/types";
import Button from "client/common/components/buttons/Button";
import TextInput from "client/common/components/inputs/TextInput";
import { useUpdatePatientMutation } from "client/accounts/hooks";
import FetchErrorAlert from "client/common/components/errors/FetchErrorAlert";
import Select from "client/common/components/inputs/Select";
import { useInvalidateOnboarding } from "client/onboarding/hooks/invalidate";

import StepLayout from "../OnboardingSubscriptionsStepLayout";

import styles from "../../styles/OnboardingSubscriptionsPatientMissingFieldsStep.module.less";

const getFieldDisplayName = (field: PatientMissingCheckoutField) => {
  const mapping: Partial<Record<PatientMissingCheckoutField, string>> = {
    name: "name",
    weight_in_units: "weight",
    birth_year: "birthday",
  };
  return mapping[field];
};

const OnboardingSubscriptionsPatientMissingFieldsStep = ({
  subscription,
  onNext,
  isLoadingNext,
  stepper,
}: OnboardingDetailSubscriptionStepParams) => {
  const { patient } = subscription;
  const [values, updateField] = useFormState<Partial<IPatientWrite>>({});
  const invalidateOnboarding = useInvalidateOnboarding();

  const {
    mutate: updatePatient,
    isPending: isUpdating,
    error: updateError,
  } = useUpdatePatientMutation(patient.id, {
    onSuccess: () => {
      invalidateOnboarding();
      onNext();
    },
  });

  const missingFields = patient.missing_checkout_fields;
  const primaryMissingFieldName = getFieldDisplayName(missingFields[0]);
  const hasAllBirthdayFields = values.birth_day && values.birth_month && values.birth_year;
  const hasRequiredFields =
    missingFields.every((f) => values[f]) &&
    !!(!missingFields.includes("birth_year") || hasAllBirthdayFields);
  const canSubmit = !isUpdating && !isLoadingNext && hasRequiredFields;
  const patientDisplayName = patient.name || "your pet";

  return (
    <StepLayout stepper={stepper}>
      <StepLayout.Title>
        {missingFields.length === 1
          ? `What is ${patientDisplayName}'s ${primaryMissingFieldName}?`
          : `We need a little more info about ${patientDisplayName}!`}
      </StepLayout.Title>
      {updateError && <FetchErrorAlert error={updateError} />}
      <StepLayout.Form onSubmit={() => updatePatient(values)} canSubmit={canSubmit}>
        {missingFields.includes("name") && (
          <TextInput
            value={values.name ?? ""}
            label="Name"
            onChange={updateField("name")}
            errors={updateError?.errors.name}
          />
        )}
        {missingFields.includes("weight_in_units") && (
          <TextInput
            type="number"
            value={values.weight_in_units ? values.weight_in_units.toString() : ""}
            label={`Weight (${getWeightUnitOfMeasureLabel(patient.weight_unit_of_measure)})`}
            onChange={(v) => updateField("weight_in_units")(parseNumber(v))}
            errors={updateError?.errors.weight_in_units}
          />
        )}
        {missingFields.includes("birth_year") && (
          <>
            <div className={styles.birthMonthDay}>
              <Select
                label="Birth Month"
                value={values.birth_month ? values.birth_month.toString() : ""}
                onChange={(v) => updateField("birth_month")(parseNumber(v))}
                errors={updateError?.errors.birth_month}
                className={styles.monthInput}
              >
                {MONTH_SHORT_NAMES.map((month, index) => (
                  <Select.Option value={`${index + 1}`} key={month}>
                    {month}
                  </Select.Option>
                ))}
              </Select>
              <TextInput
                type="number"
                label="Birth Day"
                value={values.birth_day ? values.birth_day.toString() : ""}
                maxLength={2}
                onChange={(v) => updateField("birth_day")(parseNumber(v))}
                errors={updateError?.errors.birth_day}
              />
            </div>
            <TextInput
              type="number"
              maxLength={4}
              value={values.birth_year ? values.birth_year.toString() : ""}
              label="Birth Year"
              onChange={(v) => updateField("birth_year")(parseNumber(v))}
              errors={updateError?.errors.birth_year}
            />
          </>
        )}
        <StepLayout.Footer>
          <Button type="submit" disabled={!canSubmit} isLoading={isUpdating || isLoadingNext}>
            Next
          </Button>
        </StepLayout.Footer>
      </StepLayout.Form>
    </StepLayout>
  );
};

export default OnboardingSubscriptionsPatientMissingFieldsStep;
